import { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router';
import {
  BookOpen,
  Building2,
  KeyRound,
  LayoutDashboard,
  LogOut,
  Mail,
  Megaphone,
  Menu,
  Package,
  Settings,
  ShieldCheck,
  UserCircle2,
  X,
} from 'lucide-react';
import { BRAND } from '@shripos/shared/brand';
import { ADMIN_ROLE_NAMES, adminCan } from '@shripos/shared/constants';
import { cn } from '@shripos/ui';
import { api, queryClient } from '../lib/api.js';
import { useSession } from '../stores/session.js';

const NAV = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/clients', label: 'Clients', icon: Building2, perm: 'clients.view' },
  { to: '/emails', label: 'Email logs', icon: Mail, perm: 'emails.view' },
  { to: '/announcements', label: 'Announcements', icon: Megaphone, perm: 'clients.view' },
  { to: '/plans', label: 'Plans', icon: Package, perm: 'plans.view' },
  { to: '/api-docs', label: 'API docs', icon: BookOpen, perm: 'integrations.view' },
  { to: '/admins', label: 'Admin users', icon: ShieldCheck, perm: 'admins.manage' },
  { to: '/settings', label: 'Settings', icon: Settings, perm: 'clients.view' },
];

const IDLE_LIMIT_MS = 30 * 60_000;

export async function logout(reason = 'logout') {
  try {
    await api.post('/auth/logout');
  } catch {
    /* already signed out server-side */
  }
  queryClient.clear();
  useSession.getState().signedOut(reason);
}

/** Mirrors the server's idle timeout so an unattended screen signs itself out. */
function useIdleLogout() {
  useEffect(() => {
    let timer;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => logout('idle'), IDLE_LIMIT_MS);
    };
    const events = ['pointerdown', 'keydown', 'scroll'];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, []);
}

function Sidebar({ onNavigate }) {
  const admin = useSession((s) => s.admin);
  const navigate = useNavigate();
  return (
    <div className="flex h-full flex-col bg-slate-900 text-slate-300">
      <div className="flex h-16 items-center gap-2.5 px-5">
        <img src="/favicon.svg" alt="" className="size-8" />
        <div className="leading-tight">
          <p className="font-semibold text-white">{BRAND.productName}</p>
          <p className="text-xs text-slate-400">Admin portal</p>
        </div>
      </div>
      <nav className="flex-1 space-y-0.5 px-3 py-2">
        {NAV.filter((n) => !n.perm || adminCan(admin?.role, n.perm)).map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                isActive ? 'bg-slate-800 text-white' : 'hover:bg-slate-800/60 hover:text-white',
              )
            }
          >
            <n.icon className="size-4.5" aria-hidden />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-slate-800 p-3">
        <button
          onClick={() => {
            onNavigate?.();
            navigate('/account');
          }}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm hover:bg-slate-800/60"
        >
          <UserCircle2 className="size-8 text-slate-500" aria-hidden />
          <span className="min-w-0">
            <span className="block truncate font-medium text-white">{admin?.name}</span>
            <span className="block truncate text-xs text-slate-400">{ADMIN_ROLE_NAMES[admin?.role]}</span>
          </span>
        </button>
        <div className="mt-1 flex gap-1">
          <button
            onClick={() => {
              onNavigate?.();
              navigate('/account');
            }}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs hover:bg-slate-800/60"
          >
            <KeyRound className="size-3.5" /> Account
          </button>
          <button
            onClick={() => logout()}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs hover:bg-slate-800/60"
          >
            <LogOut className="size-3.5" /> Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

export function Layout() {
  const [open, setOpen] = useState(false);
  useIdleLogout();
  return (
    <div className="flex min-h-full">
      <aside className="fixed inset-y-0 left-0 hidden w-64 lg:block">
        <Sidebar />
      </aside>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-72">
            <Sidebar onNavigate={() => setOpen(false)} />
            <button
              className="absolute top-4 -right-12 rounded-lg bg-white p-2"
              onClick={() => setOpen(false)}
              aria-label="Close menu"
            >
              <X className="size-5" />
            </button>
          </aside>
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col lg:pl-64">
        <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-slate-200 bg-white/90 px-4 backdrop-blur lg:hidden">
          <button onClick={() => setOpen(true)} className="rounded-lg p-2 hover:bg-slate-100" aria-label="Open menu">
            <Menu className="size-5" />
          </button>
          <span className="font-semibold">{BRAND.adminAppName}</span>
        </header>
        <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-6 sm:px-6 lg:px-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
